import { type ClientErrorKind, type ClientErrorPayload, ClientErrorSchema } from "@unbox/api";

const CAPTURED_ERRORS: ClientErrorKind[] = ["AuthorisationError", "InternalServerError"];

/**
 * What a response is read for here, whether it came from `fetch` or from the
 * Hono client. The client's `ClientResponse` is its own structural type rather
 * than a `Response`, so it carries none of the members a runtime adds to the
 * global one; naming those members is what lets both kinds be passed.
 */
export type ApiResponse = Pick<Response, "ok" | "status" | "headers" | "json" | "text">;

export class APIError extends Error {
  constructor(
    public data: ClientErrorPayload,
    public response: ApiResponse,
  ) {
    super(data.message);
  }

  get display() {
    return this.data.display;
  }

  get capture() {
    return CAPTURED_ERRORS.includes(this.data.kind);
  }

  isKind<K extends ClientErrorPayload["kind"]>(kind: K): this is { data: Extract<ClientErrorPayload, { kind: K }> } {
    return this.data.kind === kind;
  }
}

export const handleError = async (response: ApiResponse): Promise<APIError> => {
  try {
    const json = await response.json();

    // Try to parse as AppError using Zod
    const parseResult = ClientErrorSchema.safeParse(json);

    if (parseResult.success) {
      // Successfully parsed as AppError with discriminated union type
      return new APIError(parseResult.data, response);
    }

    // If parsing failed, check for old error format
    const message: string = json.message || json.error || "An unknown error occurred";
    return new APIError({ message, kind: "InternalServerError", display: false }, response);
  } catch {
    // If JSON parsing fails, try to get text
    let message = "An unknown error occurred";
    try {
      message = await response.text();
    } catch {
      // Keep default message
    }

    return new APIError({ message, kind: "InternalServerError", display: false }, response);
  }
};
